import React from "react";
import { PageHeader } from "../components/website/pageHeader";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { FaLeaf, FaTruck, FaChartLine, FaUsers, FaLightbulb, FaHandshake } from 'react-icons/fa';

function About(props) {
  const [missionRef, isMissionVisible] = useScrollAnimation({ threshold: 0.2 });
  const [valuesRef, areValuesVisible] = useScrollAnimation({ threshold: 0.25 });
  const [storyRef, isStoryVisible] = useScrollAnimation({ threshold: 0.25 });
  const [teamRef, isTeamVisible] = useScrollAnimation({ threshold: 0.25 });

  const values = [
    {
      icon: <FaLeaf className="w-8 h-8 text-green-600" />,
      title: "Sustainability First",
      description: "We believe in creating a cleaner future through innovative electric transportation solutions that reduce emissions and environmental impact."
    },
    {
      icon: <FaTruck className="w-8 h-8 text-blue-600" />,
      title: "Fleet Excellence",
      description: "Our commitment to optimizing fleet operations helps businesses maximize efficiency while transitioning to sustainable transportation."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-purple-600" />,
      title: "Data-Driven Decisions",
      description: "We empower our clients with comprehensive analytics and insights to make informed decisions about their fleet electrification journey."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-orange-600" />,
      title: "Customer Success",
      description: "Your success is our success. We provide dedicated support and expertise to ensure smooth transitions to electric fleet operations."
    },
    {
      icon: <FaLightbulb className="w-8 h-8 text-yellow-600" />,
      title: "Innovation",
      description: "We continuously develop cutting-edge solutions for route optimization, charging infrastructure, and fleet management."
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-red-600" />,
      title: "Partnership",
      description: "We work closely with fleet operators, municipalities, and businesses to create tailored solutions for their unique needs."
    }
  ];

  return (
    <div className="min-h-screen pb-4 font-nunito space-y-16">
      <PageHeader 
        text="Who we are"
        subtext="Pioneering the future of electric fleet management through intelligent planning and optimization solutions."
        image={"./img/homepage/fleet.png"}
      />

      {/* Our Mission */}
      <div 
        ref={missionRef}
        className={`max-w-7xl mx-auto px-5 py-10 bg-white rounded-[20px] p-12 m-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
          isMissionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h1 className={`text-center text-3xl font-bold text-gray-800 mb-10 font-nunito transition-all duration-1000 ease-out transform ${
          isMissionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          Our Mission
        </h1>
        <div className="flex flex-col items-center justify-center">
          <div className="text-center text-gray-600 mb-10 font-nunito max-w-4xl">
            <p className="text-lg mb-6">
              At AmpTrans, we're dedicated to accelerating the transition to electric transportation by providing comprehensive fleet planning and optimization solutions. Our mission is to make electric fleet adoption seamless, efficient, and profitable for businesses of all sizes.
            </p>
            <p className="text-lg mb-6">
              We believe that the future of transportation is electric, and we're committed to providing the tools and expertise needed to make this transition successful. Through intelligent route optimization, charging infrastructure analysis, and advanced fleet management capabilities, we help our clients reduce costs, improve efficiency, and achieve their sustainability goals.
            </p>
            <p className="text-lg">
              Our platform empowers fleet operators to make data-driven decisions, optimize their operations, and contribute to a cleaner, more sustainable future for generations to come.
            </p>
          </div>
        </div>
      </div>

      {/* Our Story */}
      <div 
        ref={storyRef}
        className={`max-w-7xl mx-auto px-5 py-10 bg-gradient-to-r from-blue-50 to-green-50 rounded-[20px] p-12 m-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
          isStoryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h1 className={`text-center text-3xl font-bold text-gray-800 mb-10 font-nunito transition-all duration-1000 ease-out transform ${
          isStoryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          Our Story
        </h1>
        <div className="flex flex-col items-center justify-center">
          <div className="text-center text-gray-600 font-nunito max-w-4xl">
            <p className="text-lg mb-6">
              AmpTrans was founded with a vision to bridge the gap between traditional fleet operations and the electric future. Recognizing the challenges that businesses face when transitioning to electric vehicles, our team of transportation experts, data scientists, and sustainability advocates came together to create a comprehensive solution.
            </p>
            <p className="text-lg mb-6">
              Starting with deep research into fleet optimization algorithms and charging infrastructure challenges, we developed our Fleet Planner platform to address the real-world complexities of electric vehicle deployment. From route optimization that considers charging requirements to comprehensive depot management across multiple locations, our solutions are built from industry experience and customer feedback.
            </p>
            <p className="text-lg">
              Today, AmpTrans serves fleet operators across various industries - from last-mile delivery services to municipal transportation authorities - helping them successfully navigate their electrification journey while maximizing operational efficiency and sustainability impact.
            </p>
          </div>
        </div>
      </div>

      {/* Our Values */}
      <div 
        ref={valuesRef}
        className={`max-w-7xl mx-auto px-5 py-10 bg-white rounded-[20px] p-12 m-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
          areValuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h1 className={`text-center text-3xl font-bold text-gray-800 mb-10 font-nunito transition-all duration-1000 ease-out transform ${
          areValuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          Our Values
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
              <div className="flex justify-center mb-4">{value.icon}</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">{value.title}</h3>
              <p className="text-gray-600">{value.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Our Commitment */}
      <div 
        ref={teamRef}
        className={`max-w-7xl mx-auto px-5 py-10 bg-white rounded-[20px] p-12 my-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
          isTeamVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h1 className={`text-center text-3xl font-bold text-gray-800 mb-10 font-nunito transition-all duration-1000 ease-out transform ${
          isTeamVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          Our Commitment to You
        </h1>
        <div className="flex flex-col items-center justify-center">
          <div className="text-center text-gray-600 font-nunito max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Expert Support</h3>
                <p>Our team of fleet electrification experts provides dedicated support throughout your journey, from initial planning to full deployment and optimization.</p>
              </div>
              <div className="bg-green-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Continuous Innovation</h3>
                <p>We continuously enhance our platform with new features, improved algorithms, and expanded capabilities based on industry trends and customer needs.</p>
              </div>
              <div className="bg-purple-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Scalable Solutions</h3>
                <p>Whether you're managing 10 routes or 100, our platform scales with your business, offering flexible plans from basic to enterprise-level features.</p>
              </div>
              <div className="bg-orange-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Proven Results</h3>
                <p>Our clients achieve significant cost reductions, improved operational efficiency, and measurable environmental impact through our comprehensive solutions.</p>
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-800">
              Ready to transform your fleet operations? 
              <a href="/subscription" className="text-blue-600 hover:text-blue-800 transition-colors"> Get started with AmpTrans today.</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
